import { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { debounce } from "lodash";
import DSDialog from "views/system-design/DSDialog";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSBox from "views/system-design/DSBox";
import DSTextField from "views/system-design/DSTextField";
import DSTypography from "views/system-design/DSTypography";
import DSIconButton from "views/system-design/DSIconButton";
import DSChip from "views/system-design/DSChip";
import DSAvatar from "views/system-design/DSAvatar";
import DSAutocomplete from "views/system-design/DSAutocomplete";
import DSSegmentedTabs from "views/system-design/DSSegmentedTabs";
import DSSwitch from "views/system-design/DSSwitch";
import DSFormControlLabel from "views/system-design/DSFormControlLabel";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import DSPrimaryButton from "views/system-design/DSPrimaryButton";
import CloseIcon from "@mui/icons-material/Close";
import TitleIcon from "@mui/icons-material/Title";
import LinkIcon from "@mui/icons-material/Link";
import GroupIcon from "@mui/icons-material/Group";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { Author } from "api/author";
import ticketsClient from "api/tickets";
import { CreateReviewInput } from "api/reviews";
import useUsers from "components/hooks/useUsers";
import { Ticket } from "../../types";
import { getAssignableUsers, getInitials, getAvatarColor } from "../../utils";
import { useIsMobile } from "../../hooks/useIsMobile";
import SectionHeader from "../../components/shared/SectionHeader";
import { useReviewMutations } from "../hooks/useReviewQueries";
import {
  POLICY_LABEL,
  REPO_LABEL,
  ReviewPolicy,
  ReviewRequest,
} from "../types";
import { parsePullUrl } from "../utils";
import { extractApiErrorMessage } from "utils/util";

interface ReviewRequestModalProps {
  // When given, the modal edits this request instead of creating a new one.
  existing?: ReviewRequest;
  onClose: () => void;
  onSaved?: (request: ReviewRequest) => void;
}

type TicketOption = Pick<Ticket, "id" | "key" | "title">;

// Small multi-select built on chips + the single-value DS autocomplete, so
// the reviewer list reads like the ticket assignee picker. Works on ids so a
// reviewer without a matching user record is still shown and kept.
function ReviewerPicker({
  options,
  allUsers,
  value,
  onChange,
}: {
  options: Author[];
  allUsers: Author[];
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const remaining = options.filter((option) => !value.includes(option.id));
  const labelOf = (user: Author) => user.name?.trim() || user.email || user.id;
  return (
    <DSBox>
      <DSBox sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
        {value.map((id) => {
          const user = allUsers.find((candidate) => candidate.id === id);
          const label = user ? labelOf(user) : `Unknown user (${id})`;
          return (
            <DSChip
              key={id}
              avatar={
                <DSAvatar sx={{ bgcolor: getAvatarColor(label) }}>
                  {getInitials(label)}
                </DSAvatar>
              }
              label={label}
              onDelete={() => onChange(value.filter((other) => other !== id))}
            />
          );
        })}
      </DSBox>
      <DSAutocomplete<Author>
        options={remaining}
        value={null}
        blurOnSelect
        getOptionLabel={labelOf}
        onChange={(_event, selected) => {
          if (selected) onChange([...value, selected.id]);
        }}
        renderInput={(params) => (
          <DSTextField
            {...params}
            size="small"
            placeholder={
              value.length ? "Add another reviewer" : "Pick a reviewer"
            }
          />
        )}
      />
    </DSBox>
  );
}

export default function ReviewRequestModal({
  existing,
  onClose,
  onSaved,
}: ReviewRequestModalProps) {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { data: allUsers } = useUsers();
  const { create, update } = useReviewMutations();

  const [title, setTitle] = useState(existing?.title ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [prUrls, setPrUrls] = useState<string[]>(
    existing?.prs.map((pull) => pull.url) ?? [""],
  );
  // Ids are the source of truth: a reviewer whose user record is missing
  // still stays on the request instead of being dropped by a lookup.
  const [reviewerIds, setReviewerIds] = useState<string[]>(
    existing?.reviewers.map((reviewer) => reviewer.userId) ?? [],
  );
  const [openToTeam, setOpenToTeam] = useState(
    existing?.audience === "channel",
  );
  const [policy, setPolicy] = useState<ReviewPolicy>(existing?.policy ?? "any");
  const [urgent, setUrgent] = useState(existing?.priority === "urgent");
  const [ticket, setTicket] = useState<TicketOption | null>(
    existing?.ticketId
      ? { id: existing.ticketId, key: existing.ticketKey, title: "" }
      : null,
  );
  const [ticketOptions, setTicketOptions] = useState<TicketOption[]>([]);
  const [ticketSearching, setTicketSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Team members only. The requester may list themselves (useful for a
  // dry run of the flow).
  const reviewerOptions = useMemo(
    () => getAssignableUsers(allUsers ?? []),
    [allUsers],
  );

  // Only the newest search may update the options; an older, slower response
  // is ignored. Errors show inline instead of an unhandled rejection.
  const searchSeq = useRef(0);
  const [ticketError, setTicketError] = useState<string | null>(null);
  const searchTickets = useMemo(
    () =>
      debounce(async (query: string) => {
        searchSeq.current += 1;
        const seq = searchSeq.current;
        if (!query.trim()) {
          setTicketOptions([]);
          setTicketSearching(false);
          return;
        }
        setTicketSearching(true);
        setTicketError(null);
        try {
          const { tickets } = await ticketsClient.searchTickets({
            query,
            limit: 10,
            filters: { isArchived: false },
          });
          if (seq !== searchSeq.current) return;
          setTicketOptions(
            tickets.map(({ id, key, title: ticketTitle }) => ({
              id,
              key,
              title: ticketTitle,
            })),
          );
        } catch (searchError) {
          if (seq !== searchSeq.current) return;
          setTicketOptions([]);
          setTicketError(
            extractApiErrorMessage(searchError, "Could not search tickets"),
          );
        } finally {
          if (seq === searchSeq.current) setTicketSearching(false);
        }
      }, 300),
    [],
  );
  useEffect(() => () => searchTickets.cancel(), [searchTickets]);

  const parsedPulls = prUrls.map((url) => ({
    url,
    parsed: url.trim() ? parsePullUrl(url) : null,
  }));
  const filledUrls = parsedPulls.filter((entry) => entry.url.trim());
  const invalidUrl = filledUrls.some((entry) => !entry.parsed);
  const duplicateUrl =
    new Set(
      filledUrls
        .filter((entry) => entry.parsed)
        .map((entry) => `${entry.parsed?.repo}#${entry.parsed?.number}`),
    ).size !== filledUrls.filter((entry) => entry.parsed).length;

  const canSubmit =
    title.trim().length >= 3 &&
    filledUrls.length > 0 &&
    !invalidUrl &&
    !duplicateUrl &&
    (openToTeam || reviewerIds.length > 0) &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const input: CreateReviewInput = {
      title: title.trim(),
      description: description.trim(),
      prUrls: filledUrls.map((entry) => entry.url.trim()),
      reviewerIds: openToTeam ? [] : reviewerIds,
      policy: openToTeam ? "any" : policy,
      priority: urgent ? "urgent" : "normal",
      ticketId: ticket?.id ?? null,
    };
    try {
      const saved = existing
        ? await update(existing.id, input)
        : await create(input);
      onSaved?.(saved);
      onClose();
    } catch (submitError) {
      setError(extractApiErrorMessage(submitError, "Something went wrong"));
    } finally {
      setSubmitting(false);
    }
  };

  const setUrlAt = (index: number, value: string) =>
    setPrUrls((prev) => prev.map((url, i) => (i === index ? value : url)));
  const removeUrlAt = (index: number) =>
    setPrUrls((prev) =>
      prev.length === 1 ? [""] : prev.filter((_, i) => i !== index),
    );

  return (
    <DSDialog
      open
      onClose={onClose}
      fullWidth
      fullScreen={isMobile}
      maxWidth={isMobile ? undefined : "sm"}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
    >
      <DSBox
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.grey[900]} 0%, ${theme.palette.grey[700]} 100%)`,
          px: isMobile ? 2 : 3,
          py: isMobile ? 2 : 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <DSTypography
          variant="h6"
          sx={{ color: "common.white", fontWeight: 700, fontSize: 18 }}
        >
          {existing ? "Edit Review Request" : "New Review Request"}
        </DSTypography>
        <DSIconButton
          size="small"
          onClick={onClose}
          sx={{ color: "grey.400", "&:hover": { color: "common.white" } }}
        >
          <CloseIcon fontSize="small" />
        </DSIconButton>
      </DSBox>

      <DSDialogContent sx={{ p: 0 }}>
        <DSBox sx={{ px: isMobile ? 2 : 3, pt: 3, pb: 2 }}>
          <SectionHeader
            icon={<TitleIcon sx={{ fontSize: 16 }} />}
            title="What should they look at?"
          />
          <DSTextField
            placeholder="Short title for the reviewer"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            fullWidth
            autoFocus
            required
            sx={{ mb: 1.5 }}
          />
          <DSTextField
            placeholder="Context, deploy order, what to focus on (optional)"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 3 }}
          />

          <SectionHeader
            icon={<LinkIcon sx={{ fontSize: 16 }} />}
            title="Pull requests"
          />
          {parsedPulls.map((entry, index) => (
            <DSBox
              key={index}
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
            >
              <DSTextField
                placeholder="https://github.com/KlydoClock/<repo>/pull/<n>"
                value={entry.url}
                onChange={(event) => setUrlAt(index, event.target.value)}
                fullWidth
                size="small"
                error={!!entry.url.trim() && !entry.parsed}
                helperText={
                  entry.url.trim() && !entry.parsed
                    ? "Must be a PR url in a KlydoClock repo"
                    : undefined
                }
              />
              <DSChip
                label={
                  entry.parsed?.repo ? REPO_LABEL[entry.parsed.repo] : "repo"
                }
                size="small"
                variant={entry.parsed?.repo ? "filled" : "outlined"}
                sx={{ minWidth: 72, fontSize: 11 }}
              />
              <DSIconButton
                size="small"
                onClick={() => removeUrlAt(index)}
                disabled={prUrls.length === 1 && !entry.url}
              >
                <DeleteIcon fontSize="small" />
              </DSIconButton>
            </DSBox>
          ))}
          {duplicateUrl && (
            <DSTypography variant="caption" sx={{ color: "error.main" }}>
              The same PR is listed twice.
            </DSTypography>
          )}
          <DSSecondaryButton
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => setPrUrls((prev) => [...prev, ""])}
            disabled={prUrls.length >= 10}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            Add PR
          </DSSecondaryButton>

          <SectionHeader
            icon={<GroupIcon sx={{ fontSize: 16 }} />}
            title="Reviewers"
          />
          <DSFormControlLabel
            control={
              <DSSwitch
                size="small"
                checked={openToTeam}
                onChange={(event) => setOpenToTeam(event.target.checked)}
              />
            }
            label={
              <DSTypography variant="body2">
                Anyone on the team (posts to #engineers)
              </DSTypography>
            }
            sx={{ mb: 1 }}
          />
          {!openToTeam && (
            <ReviewerPicker
              options={reviewerOptions}
              allUsers={allUsers ?? []}
              value={reviewerIds}
              onChange={setReviewerIds}
            />
          )}
          <DSBox
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              mt: 1.5,
              mb: 3,
            }}
          >
            {openToTeam ? (
              <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
                The first approval completes it
              </DSTypography>
            ) : (
              <DSBox sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <DSTypography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  Who must approve
                </DSTypography>
                <DSSegmentedTabs<ReviewPolicy>
                  size="small"
                  options={[
                    { value: "any", label: POLICY_LABEL.any },
                    { value: "all", label: POLICY_LABEL.all },
                  ]}
                  value={policy}
                  onChange={(value) => value && setPolicy(value)}
                />
              </DSBox>
            )}
            <DSFormControlLabel
              control={
                <DSSwitch
                  size="small"
                  checked={urgent}
                  onChange={(event) => setUrgent(event.target.checked)}
                />
              }
              label={
                <DSTypography variant="body2">
                  Urgent (remind every hour)
                </DSTypography>
              }
            />
          </DSBox>

          <SectionHeader
            icon={<ConfirmationNumberIcon sx={{ fontSize: 16 }} />}
            title="Ticket (optional)"
          />
          <DSAutocomplete<TicketOption>
            options={ticketOptions}
            value={ticket}
            loading={ticketSearching}
            filterOptions={(options) => options}
            isOptionEqualToValue={(option, selected) =>
              option.id === selected.id
            }
            getOptionLabel={(option) =>
              option.title ? `${option.key} — ${option.title}` : option.key
            }
            onInputChange={(_event, value, reason) => {
              if (reason === "input") searchTickets(value);
            }}
            onChange={(_event, selected) => setTicket(selected)}
            noOptionsText={
              ticketError ?? "Type a ticket key or words from its title"
            }
            renderInput={(params) => (
              <DSTextField
                {...params}
                size="small"
                placeholder="Link a Klydoscope ticket"
              />
            )}
          />
        </DSBox>
      </DSDialogContent>

      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: isMobile ? 2 : 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "white.5",
        }}
      >
        {error && (
          <DSTypography variant="caption" sx={{ color: "error.main" }}>
            {error}
          </DSTypography>
        )}
        <DSBox sx={{ flex: 1 }} />
        <DSSecondaryButton onClick={onClose} sx={{ borderRadius: 2, px: 3 }}>
          Cancel
        </DSSecondaryButton>
        <DSPrimaryButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{
            borderRadius: 2,
            px: 4,
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          {submitting ? (
            <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DSCircularProgress size={16} color="inherit" />
              Saving...
            </DSBox>
          ) : existing ? (
            "Save changes"
          ) : (
            "Ask for review"
          )}
        </DSPrimaryButton>
      </DSBox>
    </DSDialog>
  );
}
